import { useEffect, useMemo, useState } from "react";
import ListStickyHeadings from "../../../components/sessions/SessionsStickyHeadings";
import SessionModel from "../../../api/models/SessionModel";
import moment from "moment-timezone";
import { ArrowLeftIcon } from "@heroicons/react/outline";
import { useTranslation } from "react-i18next";
import FullScreenModal from "../../../components/modal/FullScreenModal";
import TutorSessionInfo from "../../../components/sessions/TutorSessionsInfo";
import {
  indexTutorSessions,
  showTutorSessions,
} from "../../../api/endpoints/tutors/sessions";
import { useNavigate } from "react-router-dom";
import PlaceHolder from "../../../components/placeHolder.tsx/PlaceHolder";
import { indexTutorSchedules } from "../../../api/endpoints/tutors/schedules";
import { classNames, showApiErrorMessages } from "../../../helpers";
import CalendarScheduleModel from "../../../models/CalendarScheduleModel";
import {
  getSessionsFromScheduleSlots,
  sortByDateAndStartTime,
  groupSessionsByMonth,
  renumberGroupedSessions,
} from "../../../components/sessions/helper";
import { useSessionsNumber } from "../../../hooks/useSessionsNumber";
import SessionsPagination from "../../../components/sessions/SessionsPagination";
import { sessionPagination } from "../../../helpers/pagination";
import { useInfiniteQuery, useQuery } from "react-query";
import { ApiResponse } from "../../../api/apiResponse";
import { useEffectOnce } from "../../../hooks/useEffectOnce";
import { GetCurrentWindowSize } from "../../../hooks/useWindowSize";
import LoadingSpinner from "../../../components/LoadingSpinner";

function TutorSessionsPage() {
  const TutorLocal = useTranslation("", {
    keyPrefix: "page.tutor.dashboard",
  });
  const navigate = useNavigate();
  const [sessionsModal, setSessionsModal] = useState<boolean>(false);
  const [currentSessionId, setCurrentSessionId] = useState(0);
  const [currentInViewCardId, setCurrentInViewCard] = useState<number>(0);
  const firstSessionNumber = useSessionsNumber();
  const [disabledNext, setDisabledNext] = useState(true);
  const [disabledPrev, setDisabledPrev] = useState(true);
  const currentScreen = GetCurrentWindowSize();
  const [showListMenu, setShowListMenu] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [currentSessionNumber, setCurrentSessionNumber] =
    useState<number | undefined>(firstSessionNumber);
  const [filters, setFilters] = useState({});

  const { data: sessionsPages, fetchNextPage } = useInfiniteQuery(
    ["tutorSessions", filters],
    ({ pageParam = 1 }) => {
      setIsLoading(true);
      return indexTutorSessions({
        filters: {
          created_between: [
            moment().subtract(pageParam, "month").unix(),
            moment()
              .subtract(pageParam - 1, "month")
              .unix(),
          ],
          page_size: 50,
          ...filters,
        },
      }).then((res) => {
        setIsLoading(false);
        return res;
      });
    },
    {
      select: (data) => {
        let userSessions: SessionModel[] = [];
        return {
          pages: data.pages.map((pageResponse) => {
            if (pageResponse.isSuccess) {
              userSessions = SessionModel.fromApiResponse(pageResponse.data);
            } else {
              showApiErrorMessages(pageResponse);
            }
            return userSessions;
          }),
          pageParams: data.pageParams,
        };
      },
    }
  );

  const { data: scheduledSessions } = useQuery(
    ["tutorScheduledSessions"],
    () => indexTutorSchedules(),
    {
      select: (apiResponse: ApiResponse) => {
        if (apiResponse.isSuccess) {
          const calendarSchedule = new CalendarScheduleModel(apiResponse.data);

          if (!!calendarSchedule) {
            const scheduledSessions = getSessionsFromScheduleSlots(
              calendarSchedule.slots
            );

            return scheduledSessions;
          }
        } else {
          showApiErrorMessages(apiResponse);
        }
      },
    }
  );

  const { data: currentSession, refetch: refetchCurrentSession } = useQuery(
    ["tutorSession", currentSessionId],
    () => showTutorSessions(`${currentSessionId}`),
    {
      enabled: !!currentSessionId,
      keepPreviousData: true,
      select: (apiResponse: ApiResponse) => {
        if (apiResponse.isSuccess) {
          return SessionModel.fromApiResponse(apiResponse.data);
        } else {
          showApiErrorMessages(apiResponse);
        }
      },
    }
  );

  const sessions = useMemo(() => {
    if (sessionsPages) {
      return sortByDateAndStartTime(sessionsPages.pages.flat());
    }
    return [];
  }, [sessionsPages]);
  const firstSession = sessions[0];
  const lastDoneSession = () => {
    if (!!sessions.at(-1) && sessions.at(-1)?.status === "done")
      return sessions.at(-1);
    else return;
  };

  const onReachTop = () => {
    const currentPage = sessionsPages?.pages.length;
    if (currentPage) {
      fetchNextPage({ pageParam: currentPage + 1 }).then(() => {
        setCurrentInViewCard(firstSession?.id!);
      });
    }
  };

  const groupedSessions = groupSessionsByMonth([
    ...sessions,
    ...((Object.keys(filters).length === 0 ? scheduledSessions : []) || []),
  ]);

  useEffectOnce(() => {
    onSessionClick(lastDoneSession()?.id!);
    setCurrentInViewCard(lastDoneSession()?.id!);
  }, !!groupedSessions && !!lastDoneSession);

  const onNavigatePrevOrNext = (dir: "next" | "prev") => {
    if (!sessions.length) {
      return;
    }
    const currentSessionIndex = sessions.findIndex(
      (session) => session.id === currentSessionId
    );
    const targetSessionIndex =
      dir === "next" ? currentSessionIndex + 1 : currentSessionIndex - 1;
    handlePagination(targetSessionIndex);
  };

  const navigateToSession = (sessionId: number, sessionNumber: number) => {
    setCurrentSessionNumber(sessionNumber);
    setCurrentSessionId(sessionId);
    navigate(`/tutor/dashboard/sessions/${sessionId}`);
    setSessionsModal(false);
  };
  const handlePagination = (targetSessionIndex: number) => {
    const paginationData = sessionPagination(targetSessionIndex, sessions);
    if (!!paginationData) {
      setDisabledNext(paginationData["disabledNext"]);
      setDisabledPrev(paginationData["disabledPrev"]);
      navigateToSession(
        paginationData["sessionData"].id,
        paginationData["sessionData"].sessionNumber!
      );
    }
  };

  const onSessionClick = (sessionId: number) => {
    const targetSessionIndex = sessions.findIndex(
      (session) => session.id === sessionId
    );
    handlePagination(targetSessionIndex);
  };
  const onSearch = (val: object | null) => {
    if (!!val) {
      setFilters(val);
    } else {
      setFilters({});
    }
  };
  useEffect(() => {
    if (currentScreen !== "lg" && !lastDoneSession()) {
      setSessionsModal(true);
    }
  }, []);

  return (
    <>
      {!!sessions && (
        <aside className="relative flex-shrink-0 hidden h-screen overflow-y-auto border border-l border-gray-200 xl:flex xl:flex-col w-96">
          <ListStickyHeadings
            onClick={onSessionClick}
            sessions={groupedSessions}
            userType="tutor"
            currentInViewCardId={currentInViewCardId}
            onReachTop={onReachTop}
            onSearch={onSearch}
            filters={filters}
            enableStudents={true}
          />
        </aside>
      )}

      {Object.keys(groupedSessions).length > 0 ? (
        <>
          {/* Start Side column (hidden on smaller screens) */}

          {/* End Side column */}
          {/* Start main area*/}
          <main className="flex-1 mb-[65px] xl:mb-0 relative z-0 overflow-y-auto border focus:outline-none">
            <button
              data-cy="openSessionsModal"
              onClick={() => setSessionsModal(!sessionsModal)}
              className="flex justify-between w-full px-6 py-1 text-sm font-medium text-gray-500 border-t border-b border-gray-200 xl:hidden bg-gray-50"
            >
              <h3 className="inline-flex py-4">
                <ArrowLeftIcon className="w-5 h-5 mx-1 rtl:rotate-180" />
                {TutorLocal.t("sessionsList.readingSessions")}{" "}
              </h3>
            </button>
            <FullScreenModal setOpen={setSessionsModal} open={sessionsModal}>
              <ListStickyHeadings
                onClick={onSessionClick}
                sessions={groupedSessions}
                userType="tutor"
                currentInViewCardId={currentInViewCardId}
                onReachTop={onReachTop}
                onSearch={onSearch}
                filters={filters}
                enableStudents={true}
              />
            </FullScreenModal>
            {!!currentSession && (
              <TutorSessionInfo
                getSession={refetchCurrentSession}
                session={currentSession}
                sessionNumber={currentSessionNumber}
              />
            )}
            <SessionsPagination
              onClickNext={() => onNavigatePrevOrNext("next")}
              onClickPrev={() => onNavigatePrevOrNext("prev")}
              disabledNext={disabledNext}
              disabledPrev={disabledPrev}
              nextButtonLabel={TutorLocal.t("sessionInfo.nextSession")}
              prevButtonLabel={TutorLocal.t("sessionInfo.previousSession")}
            />
          </main>
          {/* End main area */}
        </>
      ) : isLoading ? (
        <div
          className={classNames(
            ` flex justify-center items-center w-full`
          )}
        >
          <LoadingSpinner size={32} />
        </div>
      ) : (
        <div className="flex items-center justify-center w-full h-screen ">
          <PlaceHolder title="noSestionsTitle" />
        </div>
      )}
    </>
  );
}
export default TutorSessionsPage;
